import { StepNav, TextButton } from 'slab'
import type { AcaReportingConfigStatus } from 'utils/web_api.schemas'

import AleConfirmationForm from './AleConfirmationForm'
import DownloadAndFileInstructions from './DownloadAndFileInstructions'

interface StatusConfirmationProps {
  planYear: number
  status: AcaReportingConfigStatus
  selfReportedAleStatus?: boolean
  onChangeStatus: () => void
  needsAleConfirmation?: boolean
  onAleConfirmed?: (aleStatus: boolean) => void
}

export default function StatusConfirmation ({
  status,
  selfReportedAleStatus,
  onChangeStatus,
  planYear,
  needsAleConfirmation = false,
  onAleConfirmed
}: StatusConfirmationProps) {
  const isOptedIn = status === 'opted_in'
  const isOptedOut = status === 'opted_out'
  const isExpired = status === 'expired'
  const canChangeStatus = (status === 'opted_in' || status === 'draft_ready' || status === 'opted_out') && !isExpired
  const formType = selfReportedAleStatus === true ? '1095-C' : '1095-B'

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl text-gray-800 mb-10'>
          {isOptedIn && "You've opted in to Thatch ACA filing! 🎉"}
          {isOptedOut && "You've opted out of Thatch ACA filing"}
          {isExpired && 'The deadline for opting into Thatch ACA filing has passed'}
        </h1>
        <h2 className='text-xl text-gray-800'>
          {isOptedIn && "Here's what will happen next:"}
          {isOptedOut && "Here's what you have to do next:"}
          {isExpired && 'What this means for you:'}
        </h2>
      </div>

      <div className='bg-white rounded-2xl border border-gray-200 shadow p-6'>
        {isOptedIn && (
          <div className='[&_.text-green-400]:text-thatch-600 [&_.bg-gray-200]:!bg-thatch-600 [&_li:last-child_svg_circle]:[stroke-dasharray:2_2] [&_li:last-child_svg_circle]:[stroke-linecap:round]'>
            <StepNav>
              <StepNav.Step name='Today' complete>
                <p className='pb-5 text-xs text-gray-500'>
                  We've got your opt-in and we'll compile your employee data from throughout the year
                </p>
              </StepNav.Step>

              <StepNav.Step name='Before the end of February' hideLine>
                <div className='ml-3 text-xs text-gray-500'>
                  <p>1. We'll reach back out for any corrections that need to be made prior to filing</p>
                  <p>2. Afterwards, look out for an email from Thatch — we'll notify you when your {formType}s have been sent to employees and filed with the IRS</p>
                </div>
              </StepNav.Step>
            </StepNav>
          </div>
        )}

        {isOptedOut && (
          <DownloadAndFileInstructions formType={formType} />
        )}

        {isExpired && (
          <div className='text-gray-700 space-y-6'>
            <p>
              The February 1, 2026 deadline has passed for opting into ACA reporting with Thatch. You will still receive your draft 1095 data as a .csv file that you can use with your own accountant, payroll provider, or ACA vendor.
            </p>

            {needsAleConfirmation && onAleConfirmed && (
              <>
                <div className='border-t border-gray-200 pt-6'>
                  <AleConfirmationForm
                    planYear={planYear}
                    onConfirmed={onAleConfirmed}
                    initialAleStatus={selfReportedAleStatus ?? null}
                    showFormTypeHint
                  />
                </div>

                <div className='border-t border-gray-200 pt-6'>
                  <p>
                    If you think this is a mistake, please reach out to{' '}
                    <a href='mailto:support@thatch.com' className='text-thatch-600 underline'>
                      support@thatch.com
                    </a>
                    .
                  </p>
                </div>
              </>
            )}

            {!needsAleConfirmation && (
              <p>
                If you think this is a mistake, please reach out to{' '}
                <a href='mailto:support@thatch.com' className='text-thatch-600 underline'>
                  support@thatch.com
                </a>
                .
              </p>
            )}
          </div>
        )}
      </div>
      {canChangeStatus && (
        <div className='text-left'>
          <TextButton color='gray' size='small' onClick={onChangeStatus}>
            {isOptedIn && 'Changed your mind? Opt out here.'}
            {isOptedOut && 'Changed your mind? Opt in here.'}
          </TextButton>
        </div>
      )}
    </div>
  )
}
